import { ConfirmationModalFooter } from '@/components/ConfirmationModal/Base/Footer';
import { useDeleteInvoiceReport } from '@/features/invoice_reports/components/ConfirmDeleteModal/hooks/useDeleteInvoiceReport';
import { useTranslation } from '@/i18n';
import {
  ButtonV2Props,
  ModalWithoutPortal,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useMemo } from 'react';
import styles from './ConfirmSingleDeleteModal.module.scss';
import { useConfirmDeleteModal } from './context/ConfirmDeleteModalContext';

const cx = classnames.bind(styles);

export const ConfirmSingleDeleteModal: FC = memo(() => {
  const {
    singleDeleteModalOpen,
    singleDeleteRecord,
    onCloseSingleDeleteModal,
    refetch,
    onDrawerOpen,
    onDrawerClose,
    selectedId,
  } = useConfirmDeleteModal();

  const { t } = useTranslation();

  const { deleteReport } = useDeleteInvoiceReport({
    refetch,
    onDrawerOpen,
    onDrawerClose,
    selectedId,
    onCloseDeleteModal: onCloseSingleDeleteModal,
  });

  const handleDelete = useCallback(() => {
    if (!singleDeleteRecord) return;
    return deleteReport(singleDeleteRecord.id)();
  }, [deleteReport, singleDeleteRecord]);

  const cancelButtonProps: ButtonV2Props = useMemo(
    () => ({
      isTertiary: true,
      onClick: onCloseSingleDeleteModal,
      size: 'md',
      children: t('cancel'),
    }),
    [onCloseSingleDeleteModal, t]
  );

  const deleteButtonProps: ButtonV2Props = useMemo(
    () => ({
      color: 'danger',
      onClick: handleDelete,
      size: 'md',
      children: t('delete'),
    }),
    [handleDelete, t]
  );

  return (
    <ModalWithoutPortal
      open={singleDeleteModalOpen}
      onClose={onCloseSingleDeleteModal}
      size='md'
      title={t('invoice_reports.delete_confirm_title')}
      mask
      footer={
        <ConfirmationModalFooter
          cancelButton={cancelButtonProps}
          okButton={deleteButtonProps}
        />
      }
    >
      <div>
        <p className={cx('confirm-message')}>
          {t('invoice_reports.delete_confirm')}
        </p>
        {singleDeleteRecord && (
          <div className={cx('detail-table')}>
            {singleDeleteRecord.number && (
              <div className={cx('detail-row')}>
                <span className={cx('detail-label')}>
                  {t('invoice_reports.table.columns.application_number')}
                </span>
                <span className={cx('detail-value')}>
                  {singleDeleteRecord.number}
                </span>
              </div>
            )}
            {singleDeleteRecord.title && (
              <div className={cx('detail-row')}>
                <span className={cx('detail-label')}>
                  {t('invoice_reports.table.columns.title')}
                </span>
                <span className={cx('detail-value')}>
                  {singleDeleteRecord.title}
                </span>
              </div>
            )}
            {singleDeleteRecord.apPayeeName && (
              <div className={cx('detail-row')}>
                <span className={cx('detail-label')}>
                  {t('invoice_reports.table.columns.payee')}
                </span>
                <span className={cx('detail-value')}>
                  {singleDeleteRecord.apPayeeName}
                </span>
              </div>
            )}
            {singleDeleteRecord.totalAmount && (
              <div className={cx('detail-row')}>
                <span className={cx('detail-label')}>
                  {t('invoice_reports.table.columns.total_amount')}
                </span>
                <span className={cx('detail-value')}>
                  {singleDeleteRecord.totalAmount}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </ModalWithoutPortal>
  );
});

ConfirmSingleDeleteModal.displayName = 'ConfirmSingleDeleteModal';
